import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Info, FileText } from "lucide-react";
import { TopBar, DestinationRow, EmptyState, InfoRow, ActionButton } from "./kit";
import RecordLoadFailedCard from "./RecordLoadFailedCard";
import RecordOverdueCards from "./RecordOverdueCards";
import { useChromelessOverride } from "../context/ChromelessOverrideContext";
import { useRetireTextSize } from "../context/RetireTextSizeContext";
import { AccessibilityMenu } from "./AccessibilityMenu";
import { AccountMenu } from "./AccountMenu";
import { NotificationsBell } from "./NotificationsBell";
import { OnboardingResumePrompt } from "./OnboardingResumePrompt";
import { withCreate } from "../lib/activityCreate";

/**
 * Issue #760 — "the empty record", wave 3's opening slice. Behind
 * `newUi("record")`, replaces `/dashboard` for a signed-in player with zero
 * matches and no filter active (`pages/Dashboard.jsx`'s `showEmptyRecord`
 * gate). Matches `EmptyRecord.dc.html` (round 5, sha256 7b8c77f7…) — the
 * approved drawing is the contract; the issue text is ruled by it where the
 * two differ (brief-760.md).
 *
 * SAME "the rendered screen replaces the page" mechanism #757 built for
 * `/performance` (epic #749 decision, 2026-09-23, first missed on PR #834):
 * `ChromelessOverrideContext` hides `Layout.jsx`'s sidebar, mobile nav
 * strip AND its own shared white header for exactly this mount, so this
 * component supplies its own complete page — its own navy `TopBar`, its own
 * `DestinationRow` (D92, decisions-2026-09-24.md), nothing of the shipped
 * shell behind it.
 *
 * PR #846 fix round, B3 — `loading` (default `false`) is the THIRD state
 * this one component now owns, not a second component: `pages/
 * Dashboard.jsx` renders `<RecordEmptyScreen loading />` for every
 * flag-on, unfiltered player while `GET /stats/summary` is still pending
 * (it cannot yet know whether the account is empty or populated), and
 * `<RecordEmptyScreen />` once the gate resolves `showEmptyRecord` true —
 * SAME element type, SAME position in `Dashboard.jsx`'s returned tree, so
 * React never unmounts/remounts across that transition (see
 * `Dashboard.jsx`'s own comment on `recordLoading`/`showEmptyRecord` for
 * why that matters). The root cause this fixes (#465, found as journey
 * 20 failing 3/3 with journey 08): while pending, the OLD populated
 * dashboard used to render — WITH `Layout`'s own `NotificationsBell` — and
 * the instant the summary landed the whole page swapped to this
 * component's OWN, DIFFERENT `NotificationsBell` instance; a panel opened
 * in the old bell's brief window had already stamped the read watermark,
 * then unmounted, and the notification looked read with nobody having
 * genuinely read it. `loading` renders a NEUTRAL frame — skeleton bars
 * only, no `endControls` at all (no bell, no accessibility menu, no
 * account menu) and no `OnboardingResumePrompt` — so there is nothing
 * interactive to open in the first place until the gate has actually
 * decided. Journeys must wait for the settled page (`record-empty`, or
 * whichever composition the gate resolves to) before touching any topbar
 * control; `record-loading` is this state's own testid, for exactly that
 * wait.
 *
 * The effect below fires exactly once per mount and once per unmount —
 * true across the whole `loading` -> real-content lifetime, since it is
 * one continuous mount, not two — same contract `LogGroundFlow.jsx`'s own
 * header describes.
 *
 * `data-testid="dashboard"` is kept on the outer wrapper deliberately: this
 * IS `/dashboard`, and journey 5's own assertions (`dashboard` visible,
 * `dash-log-match` visible, right after finishing — or skipping — the
 * first-run walkthrough) run on both e2e legs against whichever composition
 * is live.
 */
export default function RecordEmptyScreen({ loading = false, failed = false, onRetry }) {
  const nav = useNavigate();
  const { setActive: setChromeless } = useChromelessOverride();
  const { setRetired: setTextSizeRetired } = useRetireTextSize();
  useEffect(() => {
    setChromeless(true);
    setTextSizeRetired(true);
    // PR #846 fix round, second review (D109 correction) — this effect used
    // to call sonner's own no-id dismiss (plus staggered follow-ups) here,
    // on the theory that "the screen itself is the confirmation" for
    // whatever just navigated here. That was wrong at the source: B3 mounts
    // THIS
    // component (as `loading`) for every flag-on `/dashboard` visit, a
    // POPULATED player's included, not only the empty-record arrival D109
    // actually names — so a blanket dismiss here silently ate "Welcome
    // back!" on an ordinary sign-in too (caught live: "Welcome back!"
    // created then immediately dismissed). D109 is suppressed at the
    // SOURCE instead — `Onboarding.jsx`/`OnboardingNew.jsx` and `Register.
    // jsx`/`SignUpNew.jsx` each skip their own `toast.success(...)` only
    // when `newUi("record")` is on AND the navigation target is
    // `/dashboard` (see each file's own comment) — so this component has
    // nothing left to do about toasts at all; every OTHER toast (a
    // populated player's "Welcome back!", a deep-linked destination's own
    // confirmation) reaches this screen exactly as it would reach any
    // other page.
    return () => {
      setChromeless(false);
      setTextSizeRetired(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid="dashboard" style={{ color: "var(--nw-ink)" }}>
      <TopBar
        variant="white"
        // PR #887 review, B4 — `topbar-logo` + the home link `Layout.jsx`'s
        // own header carries everywhere else; this screen is player-only,
        // so its home is always `/dashboard`.
        homeHref="/dashboard"
        homeLabel="CricketCompass — go to your dashboard"
        // D26 (decisions-2026-09-24.md): every navy-TopBar artboard in
        // waves 2-6 carries the contrast trigger, "using the same
        // transparent, white-icon rule as the #834 fix" — `EmptyRecord.
        // dc.html` itself leaves it off (drawn before D26 was recorded),
        // and the brief rules D26 wins. `hideTextSize` swaps the "Aa"
        // trigger for the `Contrast` glyph (issue #797) — this screen's
        // own type ramp is fixed px (the kit's `.nw-*` classes), so the
        // text-size control moves nothing here either.
        //
        // B3 — `null` while `loading`: no bell, no accessibility menu, no
        // account menu at all, which is the fix itself (see this file's
        // own header) — not merely hidden, absent from the DOM, so there
        // is nothing for a journey (or a person) to open mid-transition.
        endControls={
          loading ? null : (
            <>
              <NotificationsBell compact surface="white" />
              <AccessibilityMenu hideTextSize surface="white" />
              {/* #780 (D140) — the drawn empty account circle; see AccountMenu.jsx's own docstring on the `empty` prop. */}
              <AccountMenu empty surface="white" hasRibbon />
            </>
          )
        }
        // `record-topbar` joins `log-step-1-topbar`/`-2-`/`-3-` in
        // index.css's transparent/white-icon rule for `topbar-notifications`
        // / `topbar-accessibility` on navy — see that rule's own comment.
        data-testid="record-topbar"
      />
      <DestinationRow current="record" />

      {loading ? (
        <div
          data-testid="record-loading"
          className="flex flex-col"
          style={{ gap: "var(--nw-space-log-stack)", padding: "24px 20px 20px" }}
        >
          {/* B3 — skeleton bars only, matching `EmptyRecord.dc.html`'s own
              `data-dc-note="state: loading"` block's proportions (title
              ~40% width, lede ~70%, then a rows-card skeleton with four
              label/value bar pairs) — commentary in the drawing, real UI
              here. Never a zero, never the empty copy, per the brief's own
              rule for this state. */}
          <div className="flex flex-col" style={{ gap: 8 }}>
            <SkeletonBar width="40%" />
            <SkeletonBar width="70%" />
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 12,
              padding: 16,
              background: "var(--nw-surface)",
              border: "1px solid var(--nw-line)",
              borderRadius: "var(--nw-radius-card)",
            }}
          >
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="flex items-center justify-between" style={{ gap: 12 }}>
                <SkeletonBar width="55%" />
                <SkeletonBar width="22%" />
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div
          className="flex flex-col"
          style={{ gap: "var(--nw-space-log-stack)", padding: "24px 20px 20px" }}
        >
          {/* Issue #289/#760: "the one prompt the record carries" — the
              server only ever sets `show_resume_prompt` while the account is
              still empty (OnboardingContext), so this can never collide with
              anything below it. */}
          <OnboardingResumePrompt variant="record" />

          {/* Issue #1217 — `EmptyRecord.dc.html:102-104`, "state: failed to
              load · not the same as empty" (D118). Replaces ONLY the empty
              record's content: the resume prompt stays (a failed summary
              read says nothing about onboarding). The drawn state is the
              card alone, no eyebrow. No `record-empty`, no not-an-error line, no log
              button. */}
          {failed ? (
            <>
              <RecordLoadFailedCard
                title="Couldn't load your record"
                help="Nothing has been lost — we just couldn't reach the server."
                onRetry={onRetry}
              />
            </>
          ) : (
          <>
          {/* #1150 — a new player's first planned match, once its day has passed. */}
          <RecordOverdueCards />
          <EmptyState
            data-testid="record-empty"
            data-dc-state="new player, nothing logged"
            shape="record"
            eyebrow="Your record"
            titleVariant="lg"
            title={<span data-testid="record-empty-title">Nothing on it yet</span>}
            lede="This is where your season goes. Right now it holds nothing, so it says nothing. A zero here would read as a season that went badly — and you haven't played one yet."
            rowsLabel={<span data-testid="record-empty-rows">What it will hold</span>}
            rows={[
              { label: "Matches", note: "after your first log" },
              { label: "Runs, balls, how you got out", note: "after your first log" },
              { label: "Batting average", note: "from your first match" },
              { label: "Bowling figures", note: "from your first over" },
            ]}
            footer={
              <>
                <p className="nw-help">
                  Each line stays like this until it's true. None of them starts at nought. A figure fills the
                  moment there is something behind it — no waiting, and no number of innings to get past first. When a
                  rate comes, it says what it rests on.
                </p>
                <p className="nw-help">
                  Two rows can still be empty after you have played. Not out, so there is no batting average;
                  didn't bowl, so there is no economy. Same reason both times: there is nothing to divide by —
                  and that is not a nought either.
                </p>
              </>
            }
          />

          <InfoRow icon={<Info size={16} aria-hidden="true" />} data-testid="record-empty-not-an-error">
            Nothing failed to load. There is simply nothing here yet — and we'd tell you plainly if it were the
            other thing.
          </InfoRow>

          <InfoRow icon={<FileText size={16} aria-hidden="true" />}>
            Everything above lands here and stays here. One record — there is no second stats page to go and find, and
            nothing is kept somewhere else.
          </InfoRow>

          <div className="flex flex-col" style={{ gap: 10 }}>
            {/* Ruling (fix round, gold button) — opens step 1 directly,
                the same `?new=1` deep link the Dashboard's own upcoming-
                activity chooser (`lib/activityCreate.js`'s `withCreate`)
                already uses, rather than landing in browse mode and
                requiring a second `perf-log-btn` click. `dash-log-match`
                is kept as this screen's testid (issue #760's own
                instruction), so this is still the only gold element on
                the page, matching R-003. `onClick` + `useNavigate`, not a
                wrapping `Link`, the same pattern `CoachPortalEmptyState.
                jsx`'s/`CoachSetUp.jsx`'s own `ActionButton` calls use —
                the kit component has no link mode of its own. */}
            <ActionButton
              data-testid="dash-log-match"
              onClick={() => nav(withCreate("/performance"))}
            >
              Log your first match
            </ActionButton>
            <div style={{ fontSize: 13, lineHeight: 1.5, color: "var(--nw-ink-muted)", textAlign: "center" }}>
              About two minutes, standing at the ground, with the innings still fresh.
            </div>
          </div>
          </>
          )}
        </div>
      )}
    </div>
  );
}

/** B3's own skeleton primitive — a plain rounded bar, `--nw-line` (the
 * kit's existing muted-fill token) rather than a new one, at a caller-given
 * width. No text, no testid beyond the parent's own `record-loading` —
 * nothing here is meant to be asserted on individually. */
function SkeletonBar({ width }) {
  return (
    <div
      aria-hidden="true"
      style={{ height: 14, width, borderRadius: 6, background: "var(--nw-line)" }}
    />
  );
}
